import { Box } from '@mui/material'
import { FeatureFlagIdAccountProvisioning } from 'c1gen/features'
import { TaskV1TaskServiceCreateRevokeTask } from 'c1gen/permissions'
import { AppV1AppUserServiceowner } from 'c1gen/roles'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { ActionsLabelHidden } from 'components/core/list/C1Table/ActionsLabelHidden'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxHook } from 'components/core/list/C1Table/C1TableReduxHook'
import List from 'components/core/list/index'
import { RelativeTimestamp } from 'components/core/time/display'
import { useHasBuiltinRole, useHasRole } from 'components/no-ui/capability'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppUserAccountType } from 'components/pages/app/users/account_type'
import { AppUserActionsColumn } from 'components/pages/app/users/actions/AppUserActionsCol'
import { AppUserActionOptions } from 'components/pages/app/users/actions/AppUserActionsDropdown'
import { AppUserDisplayName, urlFromAppUser } from 'components/pages/app/users/app-user-display-name'
import { AppUserRolesWithFetch } from 'components/pages/app/users/roles/AppUserRolesWithFetch'
import { AppUserStatus } from 'components/pages/app/users/status/AppUserStatus'
import { AccountListForUserStyles } from 'components/pages/user/details/styles'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { RoleID } from 'lib/constants/system'
import { useRouter } from 'next/router'
import { AppUserServiceSearchRequest } from 'pbts/c1/api/app/v1/app_user'
import { ReactElement, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchAppUsersApi } from 'redux_toolkit/app-users'

const dropdownOptions = [AppUserActionOptions.setAccountType]

export enum AccountsListColumns {
    account,
    app,
    status,
    accountType,
    accountRoles,
    lastLogin,
    actions,
}

type AccountsListActionsProps = {
    userId?: string
    showManageGrantsButton?: boolean
    showMoreOptionsButton?: boolean
    revokeVisibleOnly?: boolean
    showCredentialsAction?: boolean
}

const colsMap: Record<AccountsListColumns, C1TableReduxColumn<AppUserValue, AccountsListActionsProps>> = {
    [AccountsListColumns.account]: {
        field: 'account',
        flex: 2,
        label: <FormattedMessage id="components_accounts_list_for_user_account" defaultMessage="Account" />,
        Component: ({ row: appUser }: { row: AppUserValue }): React.ReactElement => (
            <AppUserDisplayName appUser={appUser} margin={false} />
        ),
    },
    [AccountsListColumns.app]: {
        field: 'app',
        flex: 2,
        label: <FormattedMessage id="components_accounts_list_for_user_app" defaultMessage="App" />,
        Component: ({ row: appUser }: { row: AppUserValue }): React.ReactElement => (appUser.app
            ? <AppDisplayName app={appUser.app} avatarSize="xs" />
            : <C1EmptyIndicator />),
    },
    [AccountsListColumns.status]: {
        field: 'status',
        label: <FormattedMessage id="components_accounts_list_for_user_status" defaultMessage="Status" />,
        Component: ({ row: appUser }: { row: AppUserValue }): React.ReactElement => (
            <AppUserStatus appUser={appUser} />
        ),
    },
    [AccountsListColumns.accountType]: {
        field: 'accountType',
        label: <FormattedMessage id="standing_privilege_report_account_type" defaultMessage="Account type" />,
        Component: ({ row: appUser }: { row: AppUserValue }): React.ReactElement => (
            <AppUserAccountType appUser={appUser} />
        ),
    },
    [AccountsListColumns.accountRoles]: {
        field: 'accountRoles',
        label: <FormattedMessage id="components_user_list_roles" defaultMessage="Roles" />,
        Component: ({ row: appUser }: { row: AppUserValue }): React.ReactElement => (
            <AppUserRolesWithFetch appId={appUser.appId} appUserId={appUser.id} />
        ),
    },
    [AccountsListColumns.lastLogin]: {
        field: 'lastLogin',
        label: <FormattedMessage id="standing_privilege_report_last_login" defaultMessage="Last login" />,
        Component: ({ row: appUser }: { row: AppUserValue }): React.ReactElement => <Box>
            {appUser?.lastUsedAt && appUser.lastUsedAt.seconds ?
                <RelativeTimestamp variant="date_only" date={appUser.lastUsedAt} /> :
                <C1EmptyIndicator />
            }
        </Box>,
    },
    [AccountsListColumns.actions]: {
        field: 'actions',
        label: <ActionsLabelHidden />,
        isSticky: () => true,
        shouldRender: (hasFeature, hasPermissions, hasRoles) => hasRoles(AppV1AppUserServiceowner) ||
            hasPermissions(TaskV1TaskServiceCreateRevokeTask) || hasFeature(FeatureFlagIdAccountProvisioning) || false,
        Component: ({ row: appUser, extras }) => <AppUserActionsColumn
            userId={extras.userId}
            showManageGrantsButton={extras.showManageGrantsButton}
            appUser={appUser}
            moreButtonOptions={extras.showMoreOptionsButton ? dropdownOptions : []}
            revokeVisibleOnly={extras.revokeVisibleOnly}
            showCredentialsAction={extras.showCredentialsAction}
        />,
    },
}

export const AccountsListForUser = ({ userId, columns, actionsProps = {} }:
    { userId: string, columns?: Array<AccountsListColumns>, actionsProps?: AccountsListActionsProps }): ReactElement => {
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const hasRole = useHasRole(AppV1AppUserServiceowner)
    const canNavigateToAppUser = isReadOnlySuperAdmin || hasRole
    const router = useRouter()

    const cols = useMemo(() => {
        if (!columns || columns.length === 0) {
            return Object.values(colsMap)
        }
        return columns.map((col) => colsMap[col])
    }, [columns, actionsProps]) // eslint-disable-line react-hooks/exhaustive-deps

    const table = useMemo(() => <C1TableReduxHook<AppUserServiceSearchRequest, AppUserValue>
        cols={cols}
        request={AppUserServiceSearchRequest.create({
            userIds: [userId],
            expandMask: { paths: ['*'] },
        })}
        hook={searchAppUsersApi.useSearch}
        invalidateHook={searchAppUsersApi.useInvalidateSearch}
        hookFlags={{ pageSize: 100 }}
        staticExtraProps={{ ...actionsProps, userId }}
        onRowClick={(_, row): void => {
            canNavigateToAppUser && router.push(urlFromAppUser(row))
        }}
    />, [userId, cols, actionsProps, canNavigateToAppUser, router])

    return <Box sx={AccountListForUserStyles.container}>
        <List<AppUserValue> table={table} />
    </Box>
}
